/**
 * Dicionário offline (palavras) — recurso PRO.
 * Sem o download explícito em /settings/translation, o dicionário funciona só online (R2) e
 * NADA é gravado no aparelho. Depois do download, richDictionaryService e translationService
 * passam a ler/gravar os caches locais.
 */
const FLAG = 'libritui_dict_offline_downloaded';
const RICH_CACHE = 'rich-dict-cache-v2';
const FREE_CACHE = 'free-dict-cache';
const R2 = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev';

const NAMES: Record<string, string> = {
  pt: 'portuguese', en: 'english', es: 'spanish', fr: 'french', it: 'italian',
  de: 'german', ru: 'russian', hu: 'hungarian', zh: 'chinese', ja: 'japanese', nl: 'dutch',
};
const LANG3: Record<string, string> = {
  pt: 'por', en: 'eng', es: 'spa', fr: 'fra', it: 'ita', de: 'deu', ru: 'rus', hu: 'hun',
};

export const isDictOfflineEnabled = (): boolean => {
  try { return localStorage.getItem(FLAG) === 'true'; } catch { return false; }
};

/**
 * Candidatos por par de idiomas. O dicionário rico pode estar na pasta do idioma de origem,
 * na do idioma de destino ou na raiz (ex.: alemão→português só existe em portuguese/),
 * na mesma ordem que richDictionaryService usa para ler o cache.
 */
const candidateGroups = (nativeLang: string): string[][] => {
  const tgt = nativeLang.split('-')[0].toLowerCase();
  const groups: string[][] = [];
  for (const src of Object.keys(NAMES)) {
    if (src === tgt || !NAMES[tgt]) continue;
    const file = `${NAMES[src]}_to_${NAMES[tgt]}.json`;
    groups.push([
      `${R2}/new-dictionary-flashcards/${NAMES[src]}/${file}`,
      `${R2}/new-dictionary-flashcards/${NAMES[tgt]}/${file}`,
      `${R2}/new-dictionary-flashcards/${file}`,
    ]);
    if (LANG3[src] && LANG3[tgt]) {
      const pair = `${LANG3[src]}-${LANG3[tgt]}`;
      groups.push([`${R2}/offline-dict/${src}/${pair}/${pair}.json`]);
    }
  }
  return groups;
};

type Manifest = { at: number; urls: string[]; bytes: number };
const MANIFEST_TTL = 7 * 24 * 60 * 60 * 1000;
const manifestKey = (lang: string) => `libritui_dict_offline_manifest_${lang.split('-')[0].toLowerCase()}`;

/**
 * Descobre (uma vez a cada 7 dias) quais arquivos existem no R2 para o idioma nativo.
 * Pares inexistentes geram 404 no console só nessa checagem, depois fica em cache local.
 */
const resolveManifest = async (nativeLang: string): Promise<Manifest> => {
  try {
    const cached = JSON.parse(localStorage.getItem(manifestKey(nativeLang)) || 'null') as Manifest | null;
    if (cached && Date.now() - cached.at < MANIFEST_TTL && cached.urls.length) return cached;
  } catch { /* recalcula */ }

  const found = await Promise.all(candidateGroups(nativeLang).map(async group => {
    for (const u of group) {
      try {
        const r = await fetch(u, { method: 'HEAD' });
        if (r.ok) return { url: u, bytes: Number(r.headers.get('content-length') || 0) };
      } catch { /* próximo candidato */ }
    }
    return null;
  }));
  const hits = found.filter((f): f is { url: string; bytes: number } => !!f);
  const manifest: Manifest = { at: Date.now(), urls: hits.map(h => h.url), bytes: hits.reduce((a, h) => a + h.bytes, 0) };
  try { if (manifest.urls.length) localStorage.setItem(manifestKey(nativeLang), JSON.stringify(manifest)); } catch {}
  return manifest;
};

const cacheFor = (url: string) => (url.includes('/offline-dict/') ? FREE_CACHE : RICH_CACHE);

/** Tamanho total (MB) dos dicionários do idioma nativo. null se não der para medir. */
export const estimateDictOfflineSizeMB = async (nativeLang: string): Promise<number | null> => {
  try {
    const { bytes } = await resolveManifest(nativeLang);
    return bytes > 0 ? Math.max(1, Math.round(bytes / (1024 * 1024))) : null;
  } catch { return null; }
};

export const downloadDictOffline = async (nativeLang: string, onProgress?: (pct: number) => void): Promise<void> => {
  if (!('caches' in window)) throw new Error('CacheStorage indisponível');
  const { urls } = await resolveManifest(nativeLang);
  let done = 0;
  let saved = 0;
  for (const u of urls) {
    try {
      const res = await fetch(u);
      if (res.ok) {
        const cache = await caches.open(cacheFor(u));
        await cache.put(u, res);
        saved++;
      }
    } catch { /* arquivo inexistente para o par: segue */ }
    done++;
    onProgress?.(Math.round((done / urls.length) * 100));
  }
  if (saved === 0) throw new Error('Nenhum dicionário disponível para este idioma');
  localStorage.setItem(FLAG, 'true');
};

export const deleteDictOffline = async (): Promise<void> => {
  localStorage.removeItem(FLAG);
  if ('caches' in window) {
    await caches.delete(RICH_CACHE);
    await caches.delete(FREE_CACHE);
  }
};

/** Usuário sem o download: remove dicionários que versões antigas gravaram sozinhas no aparelho. */
export const purgeAutoDownloadedDicts = async (): Promise<void> => {
  if (isDictOfflineEnabled() || typeof window === 'undefined' || !('caches' in window)) return;
  try {
    await caches.delete(RICH_CACHE);
    await caches.delete(FREE_CACHE);
  } catch { /* ignora */ }
};
